import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext();

// Normalize user.territory: map slug→id so frontend code using .id works
function normalizeUser(user) {
  if (!user) return user;
  if (user.territory && user.territory.slug && !user.territory.id) {
    return { ...user, territory: { ...user.territory, id: user.territory.slug } };
  }
  return user;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('radar_token');
    if (token) {
      authApi.me()
        .then(u => setUser(normalizeUser(u)))
        .catch(() => {
          localStorage.removeItem('radar_token');
          localStorage.removeItem('radar_refresh_token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    const handleLogout = () => { setUser(null); };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    localStorage.setItem('radar_token', data.accessToken);
    localStorage.setItem('radar_refresh_token', data.refreshToken);
    const u = normalizeUser(data.user);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch (_) {}
    localStorage.removeItem('radar_token');
    localStorage.removeItem('radar_refresh_token');
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (updates) => {
    const updated = await authApi.updateProfile(updates);
    const u = normalizeUser(updated);
    setUser(u);
    return u;
  }, []);

  // ==========================================
  // NEW: PASSWORD RESET FUNCTIONS
  // ==========================================
  const resetPasswordRequest = useCallback(async (email) => {
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Erreur lors de la demande');
    }
    return data;
  }, []);

  const resetPasswordConfirm = useCallback(async (token, newPassword) => {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Le lien est invalide ou a expiré');
    }
    return data;
  }, []);
  // ==========================================

  const isAdmin = user?.role === 'admin';
  const isOrganisateur = user?.role === 'organisateur' || isAdmin;

  // Check if user manages a specific territory (uses id which is now normalized from slug)
  const managesTerritoire = useCallback((territoryId) => {
    if (isAdmin) return true;
    const ut = user?.territory;
    if (!ut) return false;
    // Compare against both .id (normalized) and .slug (raw)
    return ut.id === territoryId || ut.slug === territoryId;
  }, [user, isAdmin]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, updateProfile,
      resetPasswordRequest, resetPasswordConfirm, // Exported the new functions here
      isAdmin, isOrganisateur, managesTerritoire,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};